/**
 * Preview element → token-path regions for the Configuration workspace's
 * click-to-edit mode.
 *
 * Each region names a widget DOM area — by the widget's stable global class
 * names (see `apps/feed-widget/src/surfaces/*` and `apps/feed-widget/src/
 * components/*`) — and lists the BrandingConfig dot-paths that style it, in
 * priority order (the first path is the field that receives focus).
 *
 * Regions are checked top to bottom, most specific first. The first region
 * whose selector matches the clicked element (via `closest()` bounded to the
 * preview frame) AND has at least one path the active editor renders wins.
 * A specific region whose paths are all hidden by the active manifest falls
 * through to broader regions, so every display type keeps coverage.
 *
 * `skipInsideControls` marks catch-all regions that must not swallow clicks
 * on interactive chrome (buttons/links): an unmapped control resolves to
 * "no editable style" instead of pretending to be the surface background.
 */
import { PREPACK_TOKEN_MANIFEST } from './surfaceTokenManifests'

const prepackPaths = [
  ...PREPACK_TOKEN_MANIFEST.essential,
  ...PREPACK_TOKEN_MANIFEST.advanced,
].map((field) => field.path)

// FeedCard's text roles share the same font controls, including the desktop
// overrides. Keep that family together so click-to-edit cannot skip the
// control that actually wins at the desktop breakpoint.
function typographyPaths(role) {
  return [
    'fontSize', 'fontSizeDesktop', 'fontWeight', 'fontFamily', 'fontStyle',
    'lineHeight', 'lineHeightDesktop', 'letterSpacing', 'textTransform',
  ].map((field) => `typography.${role}.${field}`)
}

// Generated post appearance tokens (Configuration → Generated posts). The
// widget styles every generated card from one of these groups, picked by the
// card's content origin, so a region lists every group's path and the active
// editor's paths decide which applies.
const winningTicket = (...tokens) => tokens.map((token) => `components.winningTicket.${token}`)
const GENERATED_CARD_GROUPS = ['crowdRecommendation', 'personalizedTicket']
const generatedCard = (...tokens) => GENERATED_CARD_GROUPS
  .flatMap((group) => tokens.map((token) => `components.${group}.${token}`))
// FeedCard adds this class only to generated-origin cards, so these regions
// never match a regular post.
const GENERATED = '.feed-card-personalized'

export const PREVIEW_ELEMENT_REGIONS = [
  {
    id: 'surface-heading',
    label: 'Section heading',
    selector: '.surface-heading',
    paths: [
      'components.surfaceHeading.color',
      'components.surfaceHeading.background',
      'components.surfaceHeading.fontSize',
      'components.surfaceHeading.borderRadius',
      'components.surfaceHeading.highlightColor',
      'components.surfaceHeading.paddingX',
      'components.surfaceHeading.paddingY',
      'components.surfaceHeading.marginBottom',
    ],
  },
  {
    id: 'prepack-action',
    label: 'Prepack action',
    selector: '.prepack-widget-card__cta',
    paths: [...prepackPaths.filter((path) => path.startsWith('components.cta.') || path.startsWith('layout.cta.') || path.startsWith('typography.cta.')), 'effects.ctaShadow', 'colors.primary', 'colors.buttonTextPrimary'],
  },
  {
    id: 'prepack-quoted-odds',
    label: 'Prepack quoted odds',
    selector: '.prepack-widget-card__odds-badge',
    paths: [...prepackPaths.filter((path) => path.startsWith('components.prepackOdds.')), ...typographyPaths('odds')],
  },
  {
    id: 'prepack-action-bar',
    label: 'Prepack footer',
    selector: '.prepack-widget-card__action-bar',
    paths: [...prepackPaths.filter((path) => path.startsWith('components.prepackActions.')), 'layout.cta.actionsPaddingX', 'layout.cta.actionsPaddingY'],
  },
  ...[
    ['title', 'Title', 'body'],
    ['type', 'Bet label', 'betLabel'],
    ['selection', 'Selection', 'legSelection'],
    ['market', 'Market', 'legMarket'],
  ].map(([part, label, role]) => ({
    id: `prepack-${part}`,
    label: `Prepack ${label.toLowerCase()}`,
    selector: `.prepack-widget-card__fallback-${part}`,
    paths: [...typographyPaths(role), 'colors.heading', 'colors.subheading', 'fonts.primary'],
  })),
  {
    id: 'prepack-card',
    label: 'Prepack card',
    selector: '.prepack-widget-card',
    paths: ['layout.card.borderRadius', ...prepackPaths.filter((path) => path !== 'layout.card.borderRadius' && (path.startsWith('layout.card.') || path.startsWith('layout.compactCard.') || path.startsWith('effects.card'))), 'colors.surface', 'colors.borderColor', 'borderWidth'],
  },
  {
    id: 'prepack-surface',
    label: 'Prepack surface',
    selector: '.prepacks-surface',
    skipInsideControls: true,
    paths: ['layout.feed.cardGap', 'layout.feed.paddingX', 'layout.feed.paddingY', 'colors.background', 'fonts.primary'],
  },
  {
    id: 'hero-dots',
    label: 'Navigation dots',
    selector: '.hero-surface-dots, .carousel-dots',
    paths: ['colors.primary', 'colors.subheading'],
  },
  {
    id: 'story-avatar',
    label: 'Story avatar',
    selector: '.circle-stories-avatar',
    paths: [
      'colors.primary',
      'colors.secondary',
      'colors.subheading',
      'layout.header.avatarSize',
      'colors.heading',
      'effects.avatarShadow',
    ],
  },

  /* ---- Generated post cards (WinningTicketCard; generated-origin FeedCard).
     Their selectors exist only on those cards, and their paths are only
     rendered by the whole-branding and generated-post inspectors, so they
     leave surface editing untouched. ---- */
  {
    id: 'winning-ticket-icon',
    label: 'Winning ticket icon',
    selector: '.winning-ticket-icon',
    paths: winningTicket('headerIconUrl', 'headerIconColor'),
  },
  {
    id: 'winning-ticket-header-text',
    label: 'Winning ticket header text',
    selector: '.winning-ticket-band-text',
    paths: winningTicket('headerText', 'textColor'),
  },
  {
    id: 'winning-ticket-combined-odds',
    label: 'Winning ticket combined odds',
    selector: '.winning-ticket-band-odds',
    paths: winningTicket('oddsColor'),
  },
  {
    id: 'winning-ticket-header',
    label: 'Winning ticket header',
    selector: '.winning-ticket-band',
    paths: winningTicket('headerBackground', 'headerText', 'headerIconColor'),
  },
  {
    id: 'winning-ticket-check',
    label: 'Won check marks',
    selector: '.winning-ticket-check',
    paths: winningTicket('successColor'),
  },
  {
    id: 'winning-ticket-leg-odds',
    label: 'Winning ticket leg odds',
    selector: '.winning-ticket-leg-odds',
    paths: winningTicket('oddsColor'),
  },
  {
    id: 'winning-ticket-leg-text',
    label: 'Winning ticket selections',
    selector: '.winning-ticket-leg-label, .winning-ticket-more-legs',
    paths: winningTicket('textColor'),
  },
  {
    id: 'winning-ticket-divider',
    label: 'Winning ticket divider',
    selector: '.winning-ticket-divider',
    paths: winningTicket('dividerColor'),
  },
  {
    id: 'winning-ticket-follow',
    label: 'Winning ticket Follow button',
    selector: '.winning-ticket-follow',
    paths: winningTicket('ctaBackground', 'ctaColor', 'ctaBorderRadius', 'ctaFontWeight'),
  },
  {
    id: 'winning-ticket-credit',
    label: 'Feed credit',
    selector: '.winning-ticket-credit',
    paths: winningTicket('feedCreditName', 'textColor'),
  },
  {
    id: 'winning-ticket-body',
    label: 'Winning ticket body',
    selector: '.winning-ticket-body',
    paths: winningTicket('bodyBackground', 'textColor'),
  },
  {
    id: 'winning-ticket-avatar',
    label: 'Winning ticket category icon',
    selector: '.winning-ticket-card .card-avatar, .winning-ticket-compact-avatar',
    paths: winningTicket('categoryIconUrl'),
  },
  // The author shown on a generated card is the origin's persona (its name
  // and avatar tokens override the persona creator's). Only generated cards
  // match, so a regular card's name and avatar keep the typography / avatar
  // regions below.
  {
    id: 'generated-persona-name',
    label: 'Persona name',
    selector: `${GENERATED} .card-username`,
    paths: generatedCard('personaName'),
  },
  {
    id: 'generated-persona-avatar',
    label: 'Persona avatar',
    selector: `${GENERATED} .card-avatar`,
    paths: generatedCard('personaAvatarUrl'),
  },
  {
    id: 'generated-badge',
    label: 'Origin badge',
    selector: '.feed-card-origin-badge',
    paths: generatedCard('badgeText', 'badgeBackground', 'badgeColor'),
  },
  {
    id: 'generated-odds',
    label: 'Odds',
    selector: `${GENERATED} .card-bet-odds, ${GENERATED} .leg-odds`,
    paths: generatedCard('oddsColor'),
  },
  {
    id: 'generated-cta',
    label: 'Bet button',
    selector: `${GENERATED} .card-actions-btn`,
    paths: generatedCard('ctaBackground', 'ctaColor', 'ctaBorderRadius'),
  },
  {
    id: 'generated-eyebrow',
    label: 'Accent label',
    selector: `${GENERATED} .card-editorial-eyebrow`,
    paths: generatedCard('borderColor'),
  },
  {
    id: 'generated-text',
    label: 'Post text',
    selector: [
      '.card-text',
      '.card-editorial-title',
      '.card-editorial-subtitle',
      '.card-editorial-rationale',
      '.card-content-editorial .card-text',
    ].map((part) => `${GENERATED} ${part}`).join(', '),
    paths: generatedCard('textColor'),
  },
  {
    id: 'generated-card',
    label: 'Generated card',
    selector: GENERATED,
    skipInsideControls: true,
    paths: generatedCard('cardBackground', 'borderColor'),
  },

  /* ---- Granular card interior (checked before the card fallback). In
     surface mode most of these paths are outside the curated manifest, so
     resolution falls through to the Card region — baseline mode gets the
     full granularity. ---- */
  {
    id: 'follow-button',
    label: 'Follow button',
    selector: '.card-follow-button',
    paths: [
      'colors.primary',
      'components.followButton.followingBackgroundDark',
      'components.followButton.followingBackgroundLight',
      'components.followButton.paddingX',
      'components.followButton.paddingY',
      'components.followButton.borderRadius',
      'components.followButton.fontSize',
      'components.followButton.fontWeight',
    ],
  },
  {
    id: 'share-button',
    label: 'Share button',
    selector: '.post-share-button',
    paths: [
      'components.shareButton.background',
      'components.shareButton.color',
      'components.shareButton.borderColor',
      'components.shareButton.borderWidth',
      'components.shareButton.borderRadius',
      'components.shareButton.paddingX',
      'components.shareButton.paddingY',
      'components.shareButton.gap',
      'components.shareButton.minHeight',
      'components.shareButton.fontSize',
      'components.shareButton.fontWeight',
      'components.shareButton.lineHeight',
      'components.shareButton.iconSize',
    ],
  },
  {
    id: 'promoted-badge',
    label: 'Promoted badge',
    selector: '.feed-card-promoted-badge',
    paths: [
      'components.promotedBadge.backgroundDark',
      'components.promotedBadge.backgroundLight',
      'components.promotedBadge.colorDark',
      'components.promotedBadge.colorLight',
      'components.promotedBadge.paddingX',
      'components.promotedBadge.paddingY',
      'components.promotedBadge.borderRadius',
      'components.promotedBadge.fontSize',
      'components.promotedBadge.fontWeight',
      'components.promotedBadge.letterSpacing',
    ],
  },
  {
    id: 'card-username',
    label: 'Creator name',
    selector: '.card-username, .card-header-identity',
    paths: [
      ...typographyPaths('username'),
      'colors.heading',
    ],
  },
  {
    id: 'card-avatar',
    label: 'Creator avatar',
    selector: '.card-avatar',
    paths: ['layout.header.avatarSize', 'effects.avatarShadow'],
  },
  {
    id: 'sgp-badge',
    label: 'Same game parlay badge',
    selector: '.sgp-badge, .leg-sgp-label',
    paths: [
      'components.betslip.sgpLabelBackground',
      'components.betslip.sgpLabelColor',
      'components.betslip.sgpLabelFontFamily',
      'components.betslip.sgpLabelFontSize',
      'components.betslip.sgpLabelFontWeight',
      'components.betslip.sgpLabelFontStyle',
      'components.betslip.sgpLabelLineHeight',
      'components.betslip.sgpLabelLetterSpacing',
      'components.betslip.sgpLabelTextTransform',
      'components.betslip.sgpLabelSkew',
      'layout.betslip.sgpLabelPaddingX',
      'layout.betslip.sgpLabelPaddingY',
      'layout.betslip.sgpLabelPaddingTop',
      'layout.betslip.sgpLabelPaddingBottom',
      'layout.betslip.sgpLabelRadius',
      'layout.betslip.sgpAccentWidth',
    ],
  },
  {
    id: 'live-badge',
    label: 'Live badge',
    selector: '.leg-live-badge',
    paths: ['components.betslip.liveBadgeBackground', 'components.betslip.liveBadgeColor'],
  },
  {
    id: 'status-badge',
    label: 'Status badge',
    selector: '.leg-status-badge',
    paths: [
      'components.statusBadges.wonBackground',
      'components.statusBadges.wonColor',
      'components.statusBadges.lostBackground',
      'components.statusBadges.lostColor',
      'components.statusBadges.suspendedBackground',
      'components.statusBadges.suspendedColor',
      'components.statusBadges.closedBackgroundDark',
      'components.statusBadges.closedBackgroundLight',
      'components.statusBadges.closedColor',
    ],
  },
  {
    id: 'leg-connector',
    label: 'Leg connector',
    selector: '.leg-connector',
    paths: [
      'components.betslip.connectorLineColor',
      'components.betslip.connectorMarkerColor',
      'components.betslip.connectorMarkerFill',
      'layout.betslip.connectorWidth',
      'layout.betslip.connectorDotSize',
      'layout.betslip.connectorDotBorderWidth',
      'layout.betslip.connectorCoreSize',
      'layout.betslip.connectorHaloWidth',
    ],
  },
  {
    id: 'leg-selection',
    label: 'Selection name',
    selector: '.leg-selection-name',
    paths: [
      ...typographyPaths('legSelection'),
    ],
  },
  {
    id: 'leg-market',
    label: 'Market name',
    selector: '.leg-market-name',
    paths: [
      ...typographyPaths('legMarket'),
    ],
  },
  {
    id: 'leg-event',
    label: 'Event name',
    selector: '.leg-event-name',
    paths: [
      ...typographyPaths('legEvent'),
    ],
  },
  {
    id: 'odds',
    label: 'Odds',
    selector: '.card-bet-odds, .leg-odds',
    paths: [
      ...typographyPaths('odds'),
    ],
  },
  {
    id: 'bet-topline',
    label: 'Bet header',
    selector: '.card-bet-topline',
    paths: [
      ...typographyPaths('betLabel'),
      'layout.betslip.topLinePaddingX',
      'layout.betslip.topLinePaddingY',
    ],
  },
  {
    id: 'bet-leg',
    label: 'Bet leg',
    selector: '.card-leg',
    paths: [
      'components.betslip.rowEvenDark',
      'components.betslip.rowOddDark',
      'components.betslip.rowEvenLight',
      'components.betslip.rowOddLight',
      'layout.betslip.legPaddingX',
      'layout.betslip.legPaddingY',
      'layout.betslip.legGap',
    ],
  },
  {
    id: 'card-tags',
    label: 'Tags',
    selector: '.card-tag, .card-tags',
    paths: [
      ...typographyPaths('tag'),
      'layout.tags.chipPaddingX',
      'layout.tags.chipPaddingY',
      'layout.tags.chipRadius',
      'layout.tags.chipGap',
      'layout.tags.inlineGap',
      'layout.tags.inlinePaddingX',
      'layout.tags.inlinePaddingY',
      'layout.tags.headerGap',
      'layout.tags.headerMarginLeft',
    ],
  },
  {
    id: 'copy-count',
    label: 'Copy count',
    selector: '.post-copy-count',
    paths: [
      'components.copyCount.backgroundDark',
      'components.copyCount.backgroundLight',
      'components.copyCount.colorDark',
      'components.copyCount.colorLight',
      'components.copyCount.labelColorDark',
      'components.copyCount.labelColorLight',
      'components.copyCount.iconColorDark',
      'components.copyCount.iconColorLight',
      'components.copyCount.borderColorDark',
      'components.copyCount.borderColorLight',
      'typography.copyCount.fontSize',
      'typography.copyCount.fontWeight',
      'typography.copyCount.lineHeight',
      'typography.copyCount.letterSpacing',
      'typography.copyCount.textTransform',
      'copyCountLabel',
    ],
  },
  {
    id: 'cta-warning',
    label: 'CTA warning',
    selector: '.card-actions-warning',
    paths: [
      'components.betslip.warningBackground',
      'layout.cta.warningPaddingX',
      'layout.cta.warningPaddingY',
    ],
  },
  {
    id: 'cta-button',
    label: 'Betslip button',
    selector: '.card-actions-btn, .card-actions-cta, .card-actions',
    paths: [
      'components.cta.background',
      'components.cta.color',
      'components.cta.borderWidth',
      'components.cta.borderBottomWidth',
      'components.cta.borderColor',
      'components.cta.hoverBackground',
      'layout.cta.buttonHeight',
      'layout.cta.buttonTopRadius',
      'effects.ctaShadow',
      'colors.primary',
      'colors.buttonTextPrimary',
      'buttonText',
      ...typographyPaths('cta'),
      'layout.cta.paddingX',
      'layout.cta.paddingY',
      'layout.cta.borderRadius',
      'components.cta.addedBackground',
      'components.cta.addedColor',
      'components.cta.disabledOpacity',
    ],
  },
  {
    id: 'media-controls',
    label: 'Media controls',
    selector: '.card-media-controls, .card-media-control-btn',
    paths: [
      'layout.media.controlButtonSize',
      'layout.media.controlGap',
      'layout.media.controlInset',
      'effects.mediaControlBackdrop',
      'effects.mediaControlHoverBackdrop',
    ],
  },
  {
    id: 'play-overlay',
    label: 'Play button',
    selector: '.card-media-play-overlay',
    paths: ['layout.media.playOverlaySize', 'effects.playOverlayBackdrop'],
  },
  {
    id: 'media',
    label: 'Media',
    selector: '.card-media',
    paths: [
      'layout.media.aspectRatio',
      'layout.media.objectFit',
      'layout.media.objectPosition',
      'layout.media.videoMaxHeight',
      'layout.media.gridGap',
      'layout.media.playOverlaySize',
      'effects.playOverlayBackdrop',
    ],
  },
  {
    id: 'card-text',
    label: 'Post text',
    selector: '.card-text, .card-editorial-subtitle',
    paths: [
      ...typographyPaths('body'),
    ],
  },
  {
    id: 'card-content',
    label: 'Post text spacing',
    selector: '.card-content',
    paths: [
      'layout.card.contentPaddingX',
      'layout.card.contentPaddingY',
      'layout.card.contentPaddingTop',
      'layout.card.contentPaddingBottom',
    ],
  },
  {
    id: 'card-header',
    label: 'Card header',
    selector: '.card-header',
    paths: [
      'layout.card.headerPaddingX',
      'layout.card.headerPaddingY',
      'layout.header.gap',
      'layout.header.avatarSize',
    ],
  },
  {
    id: 'compact-avatar',
    label: 'Compact card avatar',
    selector: '.compact-card-avatar',
    paths: [
      'components.compactCard.avatarBorderColor',
      'components.compactCard.avatarBorderWidth',
      'layout.compactCard.avatarSize',
      'layout.compactCard.avatarTop',
      'layout.compactCard.avatarLeft',
      'layout.compactCard.avatarGap',
      'typography.compactAvatarName.fontSize',
      'typography.compactAvatarName.fontWeight',
      'typography.compactAvatarName.lineHeight',
      'typography.compactAvatarName.letterSpacing',
      'typography.compactAvatarName.textTransform',
    ],
  },
  {
    id: 'compact-card',
    label: 'Compact card',
    selector: [
      '.compact-card',
      '.carousel-card',
      '.carousel-mobile-slide',
      '.hcarousel-slot',
      '.dynamic-carousel-small-slot',
    ].join(', '),
    paths: [
      'components.compactCard.background',
      'layout.compactCard.borderRadius',
      'layout.compactCard.widthDesktop',
      'layout.compactCard.heightDesktop',
      'layout.compactCard.widthMobile',
      'layout.compactCard.heightMobile',
      'layout.compactCard.borderWidth',
      'components.compactCard.borderColor',
      'components.compactCard.boxShadow',
      'components.compactCard.overlayTextColor',
      'components.compactCard.avatarBorderColor',
      'components.compactCard.avatarBorderWidth',
      'layout.compactCard.avatarSize',
      'effects.compactBackdrop',
    ],
  },
  {
    id: 'card',
    label: 'Card',
    selector: [
      '.feed-card',
      '.hero-surface-card',
      '.player-deck-card',
      '.horizontal-player-card',
      '.dynamic-carousel-big',
      '.vertical-stories-stage',
      '.grid-surface-tiles > *',
      '.themed-feed-list > *',
    ].join(', '),
    paths: [
      'layout.card.borderRadius',
      'layout.card.borderWidth',
      'effects.cardShadow',
      'effects.cardHoverShadow',
      'effects.cardHoverLift',
      'layout.card.headerPaddingX',
      'layout.card.contentPaddingX',
      'layout.media.videoMaxHeight',
      'colors.background',
      'colors.heading',
      'colors.subheading',
    ],
  },
  {
    id: 'surface-background',
    label: 'Surface background',
    selector: [
      '.themed-feed-container',
      '.grid-surface-container',
      '.hero-surface-container',
      '.carousel-wrapper',
      '.hcarousel-container',
      '.dynamic-carousel-container',
      '.circle-stories-container',
      '.player-deck-container',
      '.horizontal-player-container',
      '.vertical-stories-container',
      '.floating-player-surface',
    ].join(', '),
    skipInsideControls: true,
    paths: [
      'colors.background',
      'layout.feed.maxWidth',
      'layout.feed.maxWidthDesktop',
      'layout.feed.cardGap',
      'layout.feed.paddingX',
      'layout.feed.paddingY',
      'layout.feed.bannerGap',
      'layout.media.gridGap',
      'colors.primary',
    ],
  },
]

const CONTROL_SELECTOR = 'button, a, [role="button"], input, select, textarea'

/**
 * Resolves a clicked preview element to its editable token paths.
 *
 * @param {Element} target      click target inside the preview frame
 * @param {Element} frame       the preview frame root (bounds `closest()`)
 * @param {Iterable<string>} activePaths  paths the active editor renders
 *                              (surface manifest or baseline catalog)
 * @returns {{ id, label, element, paths } | null}
 */
export function resolveElementTokens(target, frame, activePaths) {
  if (!target || !frame || typeof target.closest !== 'function') return null
  const active = activePaths instanceof Set ? activePaths : new Set(activePaths || [])
  for (const region of PREVIEW_ELEMENT_REGIONS) {
    const element = target.closest(region.selector)
    if (!element || !frame.contains(element)) continue
    if (region.skipInsideControls) {
      const control = target.closest(CONTROL_SELECTOR)
      if (control && frame.contains(control)) continue
    }
    const paths = region.paths.filter((path) => active.has(path))
    if (paths.length === 0) continue
    return { id: region.id, label: region.label, element, paths }
  }
  return null
}

let locatablePathCache = null

/**
 * Union of every region's paths — the token paths that can, on at least one
 * display type, be located back to a preview element. Gates the per-field
 * "Locate in preview" affordance.
 */
export function getLocatablePaths() {
  if (!locatablePathCache) {
    locatablePathCache = new Set(
      PREVIEW_ELEMENT_REGIONS.flatMap((region) => region.paths)
    )
  }
  return locatablePathCache
}

/**
 * Reverse lookup: token path → the preview elements it styles.
 *
 * A path can belong to several regions (`colors.background` paints cards AND
 * the surface shell), so this returns the frame-bounded union of every
 * region that claims the path — the honest answer to "what does this token
 * affect?". Elements are ordered most-specific-region first, so callers can
 * scroll to `elements[0]`. Returns null when the path has no element in the
 * current preview (e.g. a compact-card token while a story block is shown).
 *
 * @param {string} path    BrandingConfig dot-path
 * @param {Element} frame  the preview frame root
 * @returns {{ id, label, elements: Element[] } | null}
 */
export function findElementsForPath(path, frame) {
  if (!path || !frame || typeof frame.querySelectorAll !== 'function') return null
  const seen = new Set()
  const elements = []
  let first = null
  for (const region of PREVIEW_ELEMENT_REGIONS) {
    if (!region.paths.includes(path)) continue
    for (const element of frame.querySelectorAll(region.selector)) {
      if (seen.has(element) || !isVisibleInPreview(element, frame)) continue
      seen.add(element)
      elements.push(element)
    }
    if (!first && elements.length > 0) first = region
  }
  if (elements.length === 0) return null
  return { id: first.id, label: first.label, elements }
}

// Responsive surfaces keep separate desktop/mobile trees in the DOM. A
// hidden copy must not consume the first scroll target or turn a miss into
// an apparent success. Inspect ancestors as well as the matching region.
function isVisibleInPreview(element, frame) {
  const view = element.ownerDocument.defaultView
  for (let node = element; node; node = node.parentElement) {
    const style = view.getComputedStyle(node)
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false
    if (node === frame) break
  }
  return true
}
